import React, { useCallback, useState } from 'react'
import { Formik, useFormikContext } from 'formik'
import { useDispatch } from 'react-redux'
import { UserType } from 'generated/graphql'
import { _, withCapture } from 'utils'
import ConfirmModal from 'components/ConfirmModal'
import * as Yup from 'yup'
import TextField from '../forms/components/TextField'
import { showNotification } from '../notifications'
import { showErrorNotification } from '../notifications/notificationsSlice'
import { addOrganizationAdmin } from './graphql/mutation'

export const InvitationValidation = Yup.object().shape({
    email: Yup.string().email().required(),
})

interface InviteUserProps {
  organizationId: string
  close: () => void
  afterSubmit: (addedParticipant: UserType) => void
}

interface InviteUserModalProps {
  close: () => void
  disabled: boolean
}
interface InviteUserFormProps {
  disabled: boolean
}

const InviteUserForm = ({ disabled }: InviteUserFormProps) => (
    <>
        <TextField
            name="email"
            isDisabled={disabled}
            label={_('groupsession.participant.form.email')}
            isRequired
        />
    </>
)

const InviteUserModal = ({ close, disabled }: InviteUserModalProps) => {
    const { submitForm, isValid } = useFormikContext()
    return (
        <>
            <ConfirmModal
                close={close}
                title={_('organization.user.invite_dialog.title')}
                confirmDescription={(<InviteUserForm disabled={disabled} />)}
                confirmText={_('organization.user.invite_dialog.send_invitation')}
                disabledConfirm={!isValid}
                onBeforeConfirm={submitForm}
                variantConfirm="success"
            />
        </>
    )
}

const AddParticipant = ({ organizationId, close, afterSubmit }: InviteUserProps) => {
    const [loading, setLoading] = useState(false)
    const dispatch = useDispatch()

    const handleSubmit = useCallback(async ({ email }) => {
        if (!organizationId) {
            return
        }

        setLoading(true)
        const response = await withCapture(
            async () => await addOrganizationAdmin({ email, organizationId })
        )()
        setLoading(false)

        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: _('form.notification.update_success'),
            }))
            afterSubmit?.(response.data as any as UserType)

            return true
        }

        dispatch(showErrorNotification(response.errors))
        return false
    }, [afterSubmit, dispatch, organizationId])

    return (
        <Formik
            initialValues={{ email: '' }}
            validateOnMount
            validationSchema={InvitationValidation}
            onSubmit={handleSubmit}
        >
            <InviteUserModal
                close={close}
                disabled={loading}
            />
        </Formik>
    )
}

export default AddParticipant
